import React, { useMemo } from "react";
import { Stack, Typography, Chip, Divider, Box, useTheme } from "@mui/material";
import { IconCheck } from "@tabler/icons-react";
import { CardContainerRetro } from "../Container/CardContainerRetro";
import { useTranslation } from "react-i18next";

export const PlanCard = ({ plan, billingMode = "monthly", isMobile = false }) => {
	const { t } = useTranslation();
	const theme = useTheme();

	const activeBilling = useMemo(() => {
		if (!Array.isArray(plan?.billing)) return null;
		return (
			plan.billing.find((option) => option.period === billingMode) ??
			plan.billing[0] ??
			null
		);
	}, [plan?.billing, billingMode]);

	const cardBackground = plan?.highlighted
		? "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(79,70,229,0.04) 100%)"
		: theme.palette.background.paper;
	const cardPadding = isMobile ? "24px" : "32px";

	return (
		<CardContainerRetro
			w="100%"
			rd={20}
			weight={plan?.highlighted ? "bold" : "light"}
			style={{
				padding: 0,
				background: cardBackground,
				display: "flex",
			}}
		>
			<Stack spacing={3} sx={{ padding: cardPadding, width: "100%" }}>
				<Stack spacing={1.5}>
					{plan?.badgeKey && (
						<Chip
							color="primary"
							size="small"
							label={t(plan.badgeKey)}
							sx={{ alignSelf: "flex-start" }}
						/>
					)}
					<Typography variant="h5" fontWeight={700} color="text.primary">
						{t(plan.titleKey)}
					</Typography>
					{plan?.descriptionKey && (
						<Typography variant="body2" color="text.secondary">
							{t(plan.descriptionKey)}
						</Typography>
					)}
				</Stack>

				{activeBilling && (
					<Stack spacing={0.5}>
						<Typography variant={plan?.highlighted ? "h4" : "h5"} fontWeight={700} color="text.primary">
							{t(activeBilling.priceKey)}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t(activeBilling.labelKey)}
						</Typography>
						{activeBilling.noteKey && (
							<Typography variant="caption" color="text.secondary">
								{t(activeBilling.noteKey)}
							</Typography>
						)}
					</Stack>
				)}

				<Divider />

				<Stack spacing={1.25}>
					<Typography variant="subtitle2" fontWeight={600} color="text.secondary">
						{t("pricing.featuresTitle")}
					</Typography>
					<Stack spacing={1.25}>
						{(plan?.features ?? []).map((featureKey) => (
							<Stack
								key={`${plan.id}-${featureKey}`}
								direction="row"
								spacing={1.5}
								alignItems="center"
							>
								<Box
									sx={{
										width: 28,
										height: 28,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										borderRadius: "50%",
										backgroundColor: "rgba(99, 102, 241, 0.12)",
										color: "rgba(79, 70, 229, 1)",
									}}
								>
									<IconCheck size={16} stroke={2.2} />
								</Box>
								<Typography variant="body2" color="text.primary">
									{t(featureKey)}
								</Typography>
							</Stack>
						))}
					</Stack>
				</Stack>
			</Stack>
		</CardContainerRetro>
	);
};

export default PlanCard;
